import React from "react";
import { motion } from "framer-motion";
import CountUp from "./CountUp";

/**
 * LendScoreGauge — animated circular gauge with a 3D-motion feel.
 * Uses a conic-gradient background for depth, a rotating outer ring, and
 * animated stroke offset for the score arc. Fully theme-aware (dark + light).
 */
const scoreTier = (score, language) => {
    if (score >= 80) return { key: "lendscore_tier_excellent", color: "#7DD3B0", ring: "rgba(46,196,182,0.35)" };
    if (score >= 60) return { key: "lendscore_tier_good", color: "#F9C77E", ring: "rgba(255,191,105,0.30)" };
    if (score >= 40) return { key: "lendscore_tier_fair", color: "#F9AE7C", ring: "rgba(255,159,28,0.30)" };
    return { key: "lendscore_tier_poor", color: "#F97C7C", ring: "rgba(231,111,81,0.30)" };
};

// Simple i18n lookup for the tier label (avoids adding another prop)
const tierLabel = (key, language) => {
    const map = {
        es: {
            lendscore_tier_excellent: "Excelente",
            lendscore_tier_good: "Bien",
            lendscore_tier_fair: "Aceptable",
            lendscore_tier_poor: "En construcción",
        },
        en: {
            lendscore_tier_excellent: "Excellent",
            lendscore_tier_good: "Good",
            lendscore_tier_fair: "Fair",
            lendscore_tier_poor: "Building",
        },
    };
    return map[language]?.[key] || key;
};

export const LendScoreGauge = ({ score = 0, size = 220, language = "en" }) => {
    const clamped = Math.max(0, Math.min(100, Math.round(score || 0)));
    const tier = scoreTier(clamped, language);

    // SVG arc geometry — full circle
    const strokeW = 14;
    const r = (size - strokeW) / 2;
    const c = 2 * Math.PI * r;
    const dashOffset = c - (clamped / 100) * c;
    const center = size / 2;

    const gradId = `lend-grad-${tier.color.replace("#", "")}`;
    const shadowId = `lend-shadow-${tier.color.replace("#", "")}`;

    return (
        <div className="relative mx-auto flex items-center justify-center" style={{ width: size, height: size }}>
            {/* Radial ambient glow */}
            <motion.div
                aria-hidden
                className="absolute inset-0 rounded-full"
                style={{
                    background: `radial-gradient(circle at 50% 50%, ${tier.ring} 0%, transparent 70%)`,
                    filter: "blur(20px)",
                }}
                animate={{ scale: [1, 1.05, 1], opacity: [0.7, 0.9, 0.7] }}
                transition={{ duration: 4.5, ease: "easeInOut", repeat: Infinity }}
            />

            {/* Rotating outer tick ring — gives the 3D "spinning satellite" feel */}
            <motion.svg
                aria-hidden
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 32, ease: "linear", repeat: Infinity }}
            >
                <g stroke={tier.color} strokeWidth="1.5" strokeLinecap="round" opacity="0.45">
                    {Array.from({ length: 60 }).map((_, i) => {
                        const angle = (i / 60) * Math.PI * 2;
                        const inner = r + strokeW / 2 + 6;
                        const outer = inner + (i % 5 === 0 ? 6 : 3);
                        return (
                            <line
                                key={i}
                                x1={center + Math.cos(angle) * inner}
                                y1={center + Math.sin(angle) * inner}
                                x2={center + Math.cos(angle) * outer}
                                y2={center + Math.sin(angle) * outer}
                            />
                        );
                    })}
                </g>
            </motion.svg>

            {/* Main gauge */}
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="relative -rotate-90">
                <defs>
                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={tier.color} stopOpacity="0.55" />
                        <stop offset="100%" stopColor={tier.color} stopOpacity="1" />
                    </linearGradient>
                    <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={tier.color} floodOpacity="0.55" />
                    </filter>
                </defs>
                {/* Background track */}
                <circle
                    cx={center}
                    cy={center}
                    r={r}
                    fill="none"
                    stroke="hsl(var(--secondary))"
                    strokeOpacity="0.55"
                    strokeWidth={strokeW}
                />
                {/* Score arc */}
                <motion.circle
                    cx={center}
                    cy={center}
                    r={r}
                    fill="none"
                    stroke={`url(#${gradId})`}
                    strokeWidth={strokeW}
                    strokeLinecap="round"
                    strokeDasharray={c}
                    filter={`url(#${shadowId})`}
                    initial={{ strokeDashoffset: c }}
                    animate={{ strokeDashoffset: dashOffset }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
            </svg>

            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-[10px] font-bold tracking-[0.28em] uppercase text-[hsl(var(--muted-foreground))]">
                    {tierLabel(tier.key, language)}
                </div>
                <div
                    className="font-display font-extrabold tabular-nums leading-none tracking-tight"
                    style={{ color: tier.color, fontSize: size * 0.28 }}
                >
                    <CountUp value={clamped} duration={1400} format={(n) => Math.round(n).toString()} />
                </div>
                <div className="text-[10px] font-bold tracking-[0.28em] uppercase text-[hsl(var(--muted-foreground))] mt-1">
                    / 100
                </div>
            </div>
        </div>
    );
};

export default LendScoreGauge;
